import React, { useState } from 'react';
import {
    Dialog,
    DialogContent,
    Box,
    Typography,
    IconButton,
    Button,
    Divider,
    Paper,
    Checkbox,
    FormControlLabel,
    Link,
    Modal
} from '@mui/material';
import { GoogleLogin } from "@react-oauth/google";
import MicrosoftIcon from "@mui/icons-material/Microsoft";
import CloseIcon from "@mui/icons-material/Close";
import { jwtDecode } from 'jwt-decode';
import { useNavigate } from 'react-router-dom';
import api from "../axios/axiosInstance";

const modalStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    bgcolor: 'background.paper',
    borderRadius: 2,
    boxShadow: 24,
    p: 4,
    width: '90%',
    maxWidth: 600,
};

const BlogAccessCheckModal = ({ onLogin, onClose }) => {
    const [error, setError] = useState('');
    const [agreeAll, setAgreeAll] = useState(false);
    const [openTerms, setOpenTerms] = useState(false);
    const [openPrivacy, setOpenPrivacy] = useState(false);
    const navigate = useNavigate();

    const handleMicrosoftLogin = () => {
        const clientId = process.env.REACT_APP_MICROSOFT_CLIENT_ID;
        const redirectUri = `${window.location.origin}/social-callback`;
        const scope = encodeURIComponent("openid email profile User.Read");
        const authUrl = `https://login.microsoftonline.com/common/oauth2/v2.0/authorize?client_id=${clientId}&response_type=code&redirect_uri=${encodeURIComponent(redirectUri)}&response_mode=query&scope=${scope}`;
        window.location.href = authUrl;
    };

    return (
        <>
            <Dialog open onClose={onClose} fullWidth maxWidth="xs">
                <DialogContent
                    sx={{
                        p: 0,
                        bgcolor: "background.default",
                        borderRadius: 3,
                        overflow: "hidden",
                        position: "relative",
                    }}
                >
                    <IconButton
                        onClick={onClose}
                        sx={{
                            position: "absolute",
                            top: 10,
                            right: 10,
                            zIndex: 1,
                            color: "primary.main",
                        }}
                    >
                        <CloseIcon />
                    </IconButton>

                    <Paper elevation={0} sx={{ p: 4, pt: 5, textAlign: "center", bgcolor: "transparent" }}>
                        <Typography variant="h5" fontWeight="bold" sx={{ mb: 1, color: "text.primary" }}>
                            Teacher Sign In
                        </Typography>

                        <Typography variant="body2" sx={{ color: "text.secondary", mb: 3 }}>
                            Access your Gradely dashboard
                        </Typography>

                        {error && (
                            <Typography color="error" sx={{ mb: 2 }}>
                                {error}
                            </Typography>
                        )}

                        <Box sx={{ textAlign: "left", mb: 2 }}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={agreeAll}
                                        onChange={(e) => setAgreeAll(e.target.checked)}
                                        color="primary"
                                    />
                                }
                                label={
                                    <Typography variant="body2">
                                        I agree to the{' '}
                                        <Link component="button" onClick={() => setOpenTerms(true)}>
                                            Terms of Service
                                        </Link>{' '}
                                        and{' '}
                                        <Link component="button" onClick={() => setOpenPrivacy(true)}>
                                            Privacy Policy
                                        </Link>
                                    </Typography>
                                }
                            />
                        </Box>

                        <Box
                            sx={{
                                display: "flex",
                                justifyContent: "center",
                                mb: 2,
                                pointerEvents: agreeAll ? "auto" : "none",
                                opacity: agreeAll ? 1 : 0.5,
                            }}
                        >
                            <GoogleLogin
                                onSuccess={async (credentialResponse) => {
                                    const decoded = jwtDecode(credentialResponse.credential);
                                    try {
                                        const res = await api.post(
                                            "/auth/blog-access-check/",
                                            {
                                                provider: "google",
                                                id_token: credentialResponse.credential,
                                            }
                                        );

                                        const data = res.data;

                                        // 🔒 Only proceed if allowed
                                        if (data.allowed === false) {
                                            setError(data.error || "Access denied. Premium membership required.");
                                            return;
                                        }

                                        const { access, refresh, user } = data;

                                        if (typeof onLogin === "function") {
                                            onLogin(access, user);
                                        }

                                        if (onClose) onClose();
                                        navigate("/blog-editor");
                                    } catch (loginError) {
                                        // Handle 403 responses with business logic errors
                                        if (loginError.response && loginError.response.status === 403) {
                                            const errorData = loginError.response.data;
                                            setError(errorData.error || "Access denied. Premium membership required.");
                                        } else {
                                            console.error("❌ Login failed:", loginError);
                                            setError("Google login failed. Please try again.");
                                        }
                                    }
                                }}
                                onError={() => setError("Google login failed.")}
                                useOneTap={false}
                            />
                        </Box>
                    </Paper>
                </DialogContent>
            </Dialog>

            <Modal open={openTerms} onClose={() => setOpenTerms(false)}>
                <Box sx={modalStyle}>
                    <Typography variant="h6" fontWeight="bold" gutterBottom>
                        Terms of Service
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {/* Insert terms content here */}
                    </Typography>
                </Box>
            </Modal>

            <Modal open={openPrivacy} onClose={() => setOpenPrivacy(false)}>
                <Box sx={modalStyle}>
                    <Typography variant="h6" fontWeight="bold" gutterBottom>
                        Privacy Policy
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {/* Insert privacy content here */}
                    </Typography>
                </Box>
            </Modal>
        </>
    );
};

export default BlogAccessCheckModal;
